/** Snowplow 需要复用的神策身份字段。 */
export interface SensorsIdentity {
  distinct_id?: string;
  anonymous_id?: string;
}

type SensorsSDKLike = {
  store?: {
    getDistinctId?: () => unknown;
  };
  quick?: (method: string) => unknown;
};

/** 神策 distinct_id 在 Web Storage 中的缓存 key。 */
const DISTINCT_ID_STORAGE_KEY = 'vo-distinct_id';
/** 神策 anonymous_id 在 Web Storage 中的缓存 key。 */
const ANONYMOUS_ID_STORAGE_KEY = 'vo-anonymous_id';
/** 神策 distinct_id 在 cookie 中的缓存 key。 */
const DISTINCT_ID_COOKIE_KEY = 'distinct-id';
/** 神策 anonymous_id 在 cookie 中的缓存 key。 */
const ANONYMOUS_ID_COOKIE_KEY = 'anonymous-id';

/**
 * 判断原始身份值是否可用于 Snowplow 上报。
 * @param value 原始身份值
 * @returns 有效身份字符串
 */
function normalizeIdentityValue(value: unknown): string {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  const text = `${value}`.trim();
  if (!text || text === 'null' || text === 'undefined') return '';
  return text;
}

/**
 * 安全读取指定 Storage 中的身份缓存。
 * @param storage 浏览器 Storage 对象
 * @param key 缓存 key
 * @returns 有效身份字符串
 */
function readStorageValue(storage: Storage | undefined, key: string): string {
  try {
    return normalizeIdentityValue(storage?.getItem(key));
  } catch {
    return '';
  }
}

/**
 * 安全写入指定 Storage 中的身份缓存。
 * @param storage 浏览器 Storage 对象
 * @param key 缓存 key
 * @param value 身份值
 */
function writeStorageValue(storage: Storage | undefined, key: string, value: string): void {
  try {
    if (value) storage?.setItem(key, value);
  } catch {
    // 忽略隐私模式、App 容器或浏览器限制导致的存储异常。
  }
}

/**
 * 安全读取 cookie 中的身份缓存。
 * @param key cookie key
 * @returns 有效身份字符串
 */
function readCookieValue(key: string): string {
  try {
    if (typeof document === 'undefined') return '';
    const cookies = document.cookie
      ? document.cookie.split(';').map((cookie) => cookie.trim())
      : [];
    const matchedCookie = cookies.find((cookie) => cookie.startsWith(`${key}=`));
    if (!matchedCookie) return '';
    return normalizeIdentityValue(decodeURIComponent(matchedCookie.slice(key.length + 1)));
  } catch {
    return '';
  }
}

/**
 * 安全写入 cookie 身份缓存。
 * @param key cookie key
 * @param value 身份值
 */
function writeCookieValue(key: string, value: string): void {
  try {
    if (!value || typeof document === 'undefined') return;
    document.cookie = `${key}=${encodeURIComponent(value)}; path=/`;
  } catch {
    // 忽略 cookie 禁用或容器限制导致的写入异常。
  }
}

/**
 * 从一个神策 SDK 实例中读取实时身份字段。
 * @param sensors 神策 SDK 实例
 * @returns 神策身份
 */
function readIdentityFromSensors(sensors?: SensorsSDKLike): SensorsIdentity {
  try {
    const anonymousId = normalizeIdentityValue(sensors?.quick?.('getAnonymousID'));
    const distinctId = normalizeIdentityValue(sensors?.store?.getDistinctId?.()) || anonymousId;
    return {
      ...(distinctId ? { distinct_id: distinctId } : {}),
      ...(anonymousId ? { anonymous_id: anonymousId } : {}),
    };
  } catch {
    return {};
  }
}

/**
 * 获取当前窗口可访问的神策 SDK 实例列表。
 * @returns 神策 SDK 实例列表
 */
function getSensorsInstances(): SensorsSDKLike[] {
  if (typeof window === 'undefined') return [];

  const instances: SensorsSDKLike[] = [];
  try {
    if (window.sensorsDataAnalytic201505) instances.push(window.sensorsDataAnalytic201505);
    if (window.sensors) instances.push(window.sensors);
  } catch {
    // 忽略当前 window 上第三方对象访问异常。
  }

  try {
    if (window.parent && window.parent !== window) {
      if (window.parent.sensorsDataAnalytic201505) {
        instances.push(window.parent.sensorsDataAnalytic201505);
      }
      if (window.parent.sensors) instances.push(window.parent.sensors);
    }
  } catch {
    // 跨域 iframe 访问 parent 会抛错，直接跳过 parent 实例。
  }

  return instances;
}

/**
 * 从可访问的神策 SDK 实例中读取第一组有效实时身份。
 * @returns 神策实时身份
 */
function getRealtimeSensorsIdentity(): SensorsIdentity {
  const instances = getSensorsInstances();
  for (const sensors of instances) {
    const identity = readIdentityFromSensors(sensors);
    if (identity.distinct_id || identity.anonymous_id) return identity;
  }
  return {};
}

/**
 * 按 storage/cookie 优先级读取缓存身份。
 * @returns 本地缓存中的神策身份
 */
function getCachedSensorsIdentity(): SensorsIdentity {
  const safeSessionStorage = typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
  const safeLocalStorage = typeof localStorage === 'undefined' ? undefined : localStorage;
  const distinctId =
    readStorageValue(safeSessionStorage, DISTINCT_ID_STORAGE_KEY) ||
    readStorageValue(safeLocalStorage, DISTINCT_ID_STORAGE_KEY) ||
    readCookieValue(DISTINCT_ID_COOKIE_KEY);
  const anonymousId =
    readStorageValue(safeSessionStorage, ANONYMOUS_ID_STORAGE_KEY) ||
    readStorageValue(safeLocalStorage, ANONYMOUS_ID_STORAGE_KEY) ||
    readCookieValue(ANONYMOUS_ID_COOKIE_KEY);

  return {
    ...(distinctId ? { distinct_id: distinctId } : {}),
    ...(anonymousId ? { anonymous_id: anonymousId } : {}),
  };
}

/**
 * 获取 Snowplow 使用的神策身份，优先实时 SDK，兜底本地缓存和 cookie。
 * @returns 神策身份
 */
export function getSensorsIdentity(): SensorsIdentity {
  const realtimeIdentity = getRealtimeSensorsIdentity();
  if (realtimeIdentity.distinct_id || realtimeIdentity.anonymous_id) return realtimeIdentity;
  return getCachedSensorsIdentity();
}

/**
 * 将神策 SDK 实时身份同步到 sessionStorage、localStorage 和 cookie。
 * @returns 同步后的神策身份
 */
export function syncSensorsIdentity(): SensorsIdentity {
  const identity = getRealtimeSensorsIdentity();
  const safeSessionStorage = typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
  const safeLocalStorage = typeof localStorage === 'undefined' ? undefined : localStorage;

  if (identity.distinct_id) {
    writeStorageValue(safeSessionStorage, DISTINCT_ID_STORAGE_KEY, identity.distinct_id);
    writeStorageValue(safeLocalStorage, DISTINCT_ID_STORAGE_KEY, identity.distinct_id);
    writeCookieValue(DISTINCT_ID_COOKIE_KEY, identity.distinct_id);
  }

  if (identity.anonymous_id) {
    writeStorageValue(safeSessionStorage, ANONYMOUS_ID_STORAGE_KEY, identity.anonymous_id);
    writeStorageValue(safeLocalStorage, ANONYMOUS_ID_STORAGE_KEY, identity.anonymous_id);
    writeCookieValue(ANONYMOUS_ID_COOKIE_KEY, identity.anonymous_id);
  }

  return identity;
}
